import { reactive, toValue } from "vue";

import type { Ziggy } from "@solvari/common-fe/integrations";
import type { StatamicResponse } from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";
import type { ToMaybeRefs } from "@solvari/utils";

import { toSearchParams } from "@solvari/utils";

import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { cmsCollectionEntriesUrl } from "~/shared/composables/useCmsApi.ts";
import { toInternalUrl } from "~/shared/composables/useHost";
import { statamicPreviewParams } from "~/shared/composables/useStatamicPreview.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

function fetchZiggy(apiUrl: string, group: string, locale: string) {
  return kyCacheTags
    .get(`${apiUrl}/v3/ziggy/${group}`, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { "Solvari-Locale": locale },
      timeout: 20_000,
    })
    .json<Ziggy>();
}

function useZiggy(
  params: ToMaybeRefs<{ group: string; locale: LocaleMorpheus }>,
) {
  return {
    key: () => ["ziggy", reactive(params)],
    query: handleNuxtQuery(() =>
      fetchZiggy(
        toInternalUrl(`/cache-proxy/api`),
        toValue(params.group),
        toValue(params.locale),
      ),
    ),
    staleTime: Infinity,
  };
}

// Unmemoized: a memo hit replays a contract the page must own for purgeability.
// Preview forwards the token: published-only slugs → no draft route → 404.
async function getProductOverviewSlugs(locale: string): Promise<string[]> {
  const { previewSite, previewToken } = statamicPreviewParams();

  const body = await kyCacheTags
    .get(cmsCollectionEntriesUrl("pages"), {
      searchParams: toSearchParams(
        {
          fields: "slug",
          filter: {
            blueprint: "productOverview",
            site: previewSite ?? locale,
            status: previewToken ? "any" : undefined,
          },
          limit: 100,
          token: previewToken,
        },
        { nestedKeys: true },
      ),
    })
    .json<{ data: { slug: string }[] }>();

  return body.data.map((entry) => entry.slug);
}

function useProductOverviewSlugs(
  params: ToMaybeRefs<{ locale: LocaleMorpheus }>,
) {
  const { previewToken } = statamicPreviewParams();

  return {
    key: () => ["product-overview-slugs", reactive(params), previewToken],
    query: handleNuxtQuery(() =>
      getProductOverviewSlugs(toValue(params.locale)),
    ),
    staleTime: 5 * 60 * 1000,
  };
}

type UriRouteBlueprint = "performance" | "staticPage";

function fetchPageUris(
  blueprint: UriRouteBlueprint,
  locale: string,
  page: number,
) {
  const { previewSite, previewToken } = statamicPreviewParams();

  return kyCacheTags
    .get(cmsCollectionEntriesUrl("pages"), {
      searchParams: toSearchParams(
        {
          fields: "uri",
          filter: {
            blueprint,
            site: previewSite ?? locale,
            status: previewToken ? "any" : undefined,
          },
          limit: 100,
          page,
          token: previewToken,
        },
        { nestedKeys: true },
      ),
    })
    .json<{
      data: { uri: string }[];
      meta: StatamicResponse<unknown>["meta"];
    }>();
}

// All pages: a uri past the first page has no route → 404.
async function getPageUris(
  blueprint: UriRouteBlueprint,
  locale: string,
): Promise<string[]> {
  const first = await fetchPageUris(blueprint, locale, 1);
  const rest = await Promise.all(
    Array.from({ length: first.meta.last_page - 1 }, (_page, index) =>
      fetchPageUris(blueprint, locale, index + 2),
    ),
  );

  return [first, ...rest].flatMap((body) =>
    body.data.map((entry) => entry.uri.replace(/^\//, "")),
  );
}

function usePageUris(
  params: ToMaybeRefs<{ blueprint: UriRouteBlueprint; locale: LocaleMorpheus }>,
) {
  const { previewToken } = statamicPreviewParams();

  return {
    key: () => ["page-uris", reactive(params), previewToken],
    query: handleNuxtQuery(() =>
      getPageUris(toValue(params.blueprint), toValue(params.locale)),
    ),
    staleTime: 5 * 60 * 1000,
  };
}

// Through the proxy: its cache entry absorbs the per-render refetch.
async function getProvinceSlugs(locale: string): Promise<string[]> {
  const provinces = await kyCacheTags
    .get(`${toInternalUrl("/cache-proxy/api")}/v3/geo/provinces`, {
      searchParams: { localeCode: locale },
    })
    .json<{ slug: string }[]>();

  return provinces.map((province) => province.slug);
}

function useProvinceSlugs(params: ToMaybeRefs<{ locale: LocaleMorpheus }>) {
  return {
    key: () => ["province-slugs", reactive(params)],
    query: handleNuxtQuery(() => getProvinceSlugs(toValue(params.locale))),
    staleTime: Infinity,
  };
}

export {
  fetchZiggy,
  getPageUris,
  getProductOverviewSlugs,
  getProvinceSlugs,
  usePageUris,
  useProductOverviewSlugs,
  useProvinceSlugs,
  useZiggy,
};
